import React, { Component } from 'react';
import { matchPath } from 'react-router';
import Helmet from 'react-helmet';
import Compressor from 'compressorjs';

import AuthService from './AuthService';
import withAuth from './WithAuth';
import { buildConditionsList, LEGACY_CONDITIONS, LEGACY_NO_FOIL_RARITIES } from '../utils/conditions';
import constants from '../config';
import { resolveCardImage } from '../utils/resolveImage';

const Auth = new AuthService();
var SHARE_MIN_PRICE = constants.SHARE_MIN_PRICE || 10;

const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

class EditPost extends Component {
  constructor() {
    super();
    this.state = {
      productId: null,
      loading: true,
      title: '',
      description: '',
      price: '',
      quantity: '1',
      condition: 'NM',
      category: 'singles',
      photos: [],
      existingPhotos: [],
      selectedCard: null,
      connectedCards: [],
      cardImage: null,
      cardRarity: null,
      photoError: '',
      processingPhoto: false,
      allowSharing: true,
      shareCommission: 1,
      shareCommissionType: 'amount',
      acceptOffers: false,
      loadingIcon: 'none',
      errors: {},
      pricingData: null,
      pricingLoading: false,
    };
    this.handleSubmit = this.handleSubmit.bind(this);
  }

  componentDidMount() {
    var user = Auth.getProfile();
    var match = matchPath(this.props.history.location.pathname, {
      path: '/edit/:product_id',
      exact: false,
      strict: false,
    });
    var productId = match.params.product_id;
    this.loadPost(productId, user._id);
  }

  loadPost(productId, userId) {
    var self = this;
    Auth.fetch(constants.DOMAIN + '/api/v1/posts/' + productId, {
      mode: 'cors',
      method: 'GET',
    }).then(function (res) {
      if (userId !== res.user._id) {
        self.props.history.push('/');
        return;
      }

      var catParts = (res.category || '').split(' ');
      var mainCategory = catParts[0] || 'singles';
      var conditionFromCat = catParts[1] || null;

      var initialValues = {
        title: res.title || '',
        description: res.text || '',
        price: res.price != null ? String(res.price) : '',
        quantity: res.quantity != null ? String(res.quantity) : '1',
        condition: res.condition || conditionFromCat || 'NM',
        connectedCards: (res.cards && res.cards.length > 0) ? res.cards.slice() : [],
        photoCount: (res.images && res.images.length) || 0,
        allowSharing: res.allowSharing !== false,
        shareCommission: (res.shareCommissionType === 'percentage' || !res.shareCommissionType)
          ? Math.max(1, Math.floor(Number(res.price || 0) * ((res.shareCommission || 5) / 100) * 100) / 100)
          : Math.max(1, Number(res.shareCommission) || 1),
        shareCommissionType: 'amount',
        acceptOffers: res.acceptOffers === true,
      };

      self.setState({
        productId: productId,
        title: initialValues.title,
        description: initialValues.description,
        price: initialValues.price,
        quantity: initialValues.quantity,
        condition: initialValues.condition,
        category: mainCategory,
        allowSharing: initialValues.allowSharing,
        shareCommission: initialValues.shareCommission,
        acceptOffers: initialValues.acceptOffers,
        loading: false,
        _initial: initialValues,
      });

      // Load existing images as preview URLs
      if (res.images && res.images.length > 0) {
        var existingPhotos = res.images.map(function (img) {
          return { url: constants.TMP_API + img, path: img };
        });
        self.setState({ existingPhotos: existingPhotos });

        // Fetch images as blobs for re-submission
        res.images.forEach(function (img, index) {
          fetch(constants.TMP_API + img, { mode: 'cors', method: 'GET' })
            .then(function (r) { return r.blob(); })
            .then(function (blob) {
              var fileName = img.split('/').pop() || 'image.jpg';
              var file = new File([blob], fileName, { type: blob.type, lastModified: Date.now() });
              self.setState(function (prev) {
                var photos = prev.photos.slice();
                photos[index] = { file: file, preview: constants.TMP_API + img };
                return { photos: photos.filter(Boolean) };
              });
            })
            .catch(function () {});
        });
      }

      // Load card data
      if (res.cards && res.cards.length > 0) {
        self.setState({ connectedCards: res.cards });
        fetch(constants.DOMAIN + '/api/v1/guest/card/' + res.cards[0], { mode: 'cors', method: 'GET' })
          .then(function (r) { return r.json(); })
          .then(function (cardData) {
            if (cardData) {
              var cardImg = resolveCardImage(cardData.image);
              self.setState({
                selectedCard: cardData,
                cardRarity: cardData.rarity || null,
                cardImage: res.cardImage || cardImg,
              });
            }
          })
          .catch(function () {});
        self.fetchPricing(res.cards[0]);
      }
    }).catch(function (err) {
      console.log(err);
      self.setState({ loading: false });
    });
  }

  fetchPricing(cardId) {
    var self = this;
    this.setState({ pricingLoading: true });
    fetch(constants.DOMAIN + '/api/v1/guest/card/' + cardId + '/pricing', { mode: 'cors' })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        self.setState({ pricingData: data.pricing || null, pricingLoading: false });
      })
      .catch(function () {
        self.setState({ pricingData: null, pricingLoading: false });
      });
  }

  renderPricingWidget() {
    var pricingData = this.state.pricingData;
    var condition = this.state.condition;
    if (!pricingData || Object.keys(pricingData).length === 0) return null;

    var condLabel = LEGACY_CONDITIONS.find(function (c) { return c.value === condition; });
    var condName = condLabel ? condLabel.label : condition;
    var data = pricingData[condition];
    var fallback = false;
    var fallbackCondName = '';

    if (!data) {
      var bestCond = null;
      var bestCount = 0;
      Object.keys(pricingData).forEach(function (key) {
        if (pricingData[key].count > bestCount) {
          bestCount = pricingData[key].count;
          bestCond = key;
        }
      });
      if (!bestCond) return null;
      data = pricingData[bestCond];
      fallback = true;
      var fbLabel = LEGACY_CONDITIONS.find(function (c) { return c.value === bestCond; });
      fallbackCondName = fbLabel ? fbLabel.label : bestCond;
    }

    var sourceText = data.count + ' ' + (data.source === 'sales' ? (data.count === 1 ? 'sale' : 'sales') : (data.count === 1 ? 'listing' : 'listings'));

    return (
      <div
        className="tw-mb-4 tw-rounded-xl tw-border tw-border-[#e0e0e0] dark:tw-border-[#333] tw-p-4 tw-bg-gray-50 dark:tw-bg-[#1a1a1a]"
      >
        <div>
          {fallback ? (
            <div className="tw-mb-2">
              <p className="tw-text-xs tw-text-[#888] tw-mb-1 tw-mt-0">
                No {condName} data
              </p>
              <p className="tw-text-xs tw-font-semibold tw-text-[#555] dark:tw-text-[#ccc] tw-mb-0 tw-mt-0">
                {fallbackCondName} prices ({sourceText}):
              </p>
            </div>
          ) : (
            <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
              <p className="tw-text-xs tw-font-semibold tw-text-[#555] dark:tw-text-[#ccc] tw-mb-0 tw-mt-0">
                {condName} Prices
              </p>
              <p className="tw-text-[10px] tw-text-[#999] tw-mb-0 tw-mt-0">
                Based on {sourceText}
              </p>
            </div>
          )}
          <div className="tw-flex tw-justify-between tw-text-center">
            <div className="tw-flex-1">
              <p className="tw-text-[10px] tw-text-[#999] tw-uppercase tw-tracking-wide tw-mb-0.5 tw-mt-0">Low</p>
              <p className="tw-text-sm tw-font-bold tw-mb-0 tw-mt-0" style={{ color: '#c9a84c' }}>${data.low.toFixed(2)}</p>
            </div>
            <div className="tw-flex-1">
              <p className="tw-text-[10px] tw-text-[#999] tw-uppercase tw-tracking-wide tw-mb-0.5 tw-mt-0">Median</p>
              <p className="tw-text-sm tw-font-bold tw-mb-0 tw-mt-0" style={{ color: '#c9a84c' }}>${data.median.toFixed(2)}</p>
            </div>
            <div className="tw-flex-1">
              <p className="tw-text-[10px] tw-text-[#999] tw-uppercase tw-tracking-wide tw-mb-0.5 tw-mt-0">High</p>
              <p className="tw-text-sm tw-font-bold tw-mb-0 tw-mt-0" style={{ color: '#c9a84c' }}>${data.high.toFixed(2)}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  handlePriceChange(e) {
    var re = /^(?!\.?$)\d{0,4}(\.\d{0,2})?$/;
    if (e.target.value === '' || re.test(e.target.value)) {
      this.setState({ price: e.target.value });
    }
  }

  blurPrice() {
    if (this.state.price) {
      this.setState({ price: Number(this.state.price).toFixed(2) });
    }
  }

  getImageDimensions(image, obj) {
    return new Promise(function (resolve) {
      var fr = new FileReader();
      fr.onload = function () {
        var img = new Image();
        img.onload = function () {
          obj.width = img.width;
          resolve(obj);
        };
        img.src = fr.result;
      };
      fr.readAsDataURL(image);
    });
  }

  compressionWidth(width) {
    if (width > constants.MAX_IMAGE_WIDTH) return constants.MAX_IMAGE_WIDTH;
    if (width >= constants.MIN_IMAGE_WIDTH) return Math.round(width * 0.8);
    return width;
  }

  async addPhoto(event) {
    var inputEl = event.target;
    var file = inputEl.files[0];
    if (!file || this.state.photos.length >= 4) return;

    if (ACCEPTED_TYPES.indexOf(file.type) === -1) {
      this.setState({ photoError: 'Unsupported format. Use JPG, PNG, GIF, or WebP.' });
      inputEl.value = '';
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      this.setState({ photoError: 'File too large. Max 10 MB.' });
      inputEl.value = '';
      return;
    }

    this.setState({ photoError: '', processingPhoto: true });
    var tmp = {};
    tmp = await this.getImageDimensions(file, tmp);
    var self = this;

    new Compressor(file, {
      quality: constants.IMAGE_COMPRESSION,
      maxWidth: this.compressionWidth(tmp.width),
      success(result) {
        var finalFile = result instanceof File
          ? result
          : new File([result], result.name, { type: result.type, lastModified: Date.now() });

        var reader = new FileReader();
        reader.onload = function (e) {
          self.setState(function (prev) {
            return {
              photos: prev.photos.concat([{ file: finalFile, preview: e.target.result }]),
              processingPhoto: false,
            };
          });
        };
        reader.readAsDataURL(finalFile);
      },
      error: function () {
        self.setState({ photoError: 'Could not process image. Try a JPG or PNG instead.', processingPhoto: false });
      },
    });
    inputEl.value = '';
  }

  removePhoto(index) {
    this.setState(function (prev) {
      return { photos: prev.photos.filter(function (_, i) { return i !== index; }) };
    });
  }

  handleSubmit() {
    var errors = {};
    if (!this.state.title.trim()) errors.title = 'Title is required';
    if (!this.state.price || isNaN(Number(this.state.price)) || Number(this.state.price) < 0.75) {
      errors.price = 'Minimum price is $0.75';
    }
    if (Object.keys(errors).length > 0) {
      this.setState({ errors: errors });
      return;
    }

    this.setState({ loadingIcon: 'inline', errors: {} });

    var data = new FormData();
    data.append('title', this.state.title);
    data.append('text', this.state.description || '');
    data.append('price', Number(this.state.price));
    data.append('category', this.state.category + ' ' + this.state.condition);
    data.append('shippingCost', 0);
    data.append('condition', this.state.condition);
    data.append('quantity', Number(this.state.quantity) || 1);
    data.append('allowSharing', this.state.allowSharing);
    data.append('shareCommission', Math.max(1, Number(this.state.shareCommission) || 1));
    data.append('shareCommissionType', 'amount');
    data.append('acceptOffers', this.state.acceptOffers);

    if (this.state.connectedCards && this.state.connectedCards.length > 0) {
      data.append('cards', this.state.connectedCards.join(','));
    } else {
      data.append('cards', '');
    }
    if (this.state.cardImage) {
      data.append('cardImage', this.state.cardImage);
    }

    this.state.photos.forEach(function (p) {
      if (p && p.file) {
        data.append('images', p.file, p.file.name);
      }
    });

    var self = this;
    Auth.fetch(constants.DOMAIN + '/api/v1/posts/' + this.state.productId, {
      mode: 'cors',
      method: 'PATCH',
      body: data,
    }).then(function (res) {
      self.props.history.push('/product/' + res._id);
    }).catch(function (err) {
      console.log(err);
      self.setState({ loadingIcon: 'none' });
    });
  }

  deleteListing() {
    if (!window.confirm('Are you sure you want to delete this listing? This cannot be undone.')) {
      return;
    }
    this.setState({ loadingIcon: 'inline' });
    var self = this;
    Auth.fetch(constants.DOMAIN + '/api/v1/posts/' + this.state.productId, {
      mode: 'cors',
      method: 'DELETE',
    }).then(function () {
      var user = Auth.getProfile();
      self.props.history.push('/shop/' + user.username);
    }).catch(function (err) {
      console.log(err);
      self.setState({ loadingIcon: 'none' });
    });
  }

  hasChanges() {
    var initial = this.state._initial;
    if (!initial) return false;
    if (this.state.title !== initial.title) return true;
    if (this.state.description !== initial.description) return true;
    if (this.state.price !== initial.price) return true;
    if (this.state.condition !== initial.condition) return true;
    if (this.state.quantity !== initial.quantity) return true;
    if (this.state.photos.length !== initial.photoCount) return true;
    if (this.state.allowSharing !== initial.allowSharing) return true;
    if (this.state.shareCommission !== initial.shareCommission) return true;
    if (this.state.acceptOffers !== initial.acceptOffers) return true;
    return false;
  }

  render() {
    var self = this;
    var loading = this.state.loading;
    var title = this.state.title;
    var description = this.state.description;
    var price = this.state.price;
    var condition = this.state.condition;
    var photos = this.state.photos;
    var selectedCard = this.state.selectedCard;
    var cardImage = this.state.cardImage;
    var errors = this.state.errors;
    var FREE_SHIPPING_THRESHOLD = constants.FREE_SHIPPING_THRESHOLD || 10;
    var FLAT_SHIPPING_FEE = constants.FLAT_SHIPPING_FEE || 1.00;
    var loadingIcon = this.state.loadingIcon;
    var cardRarity = selectedCard ? selectedCard.rarity : null;
    var conditionsList = buildConditionsList(null, LEGACY_NO_FOIL_RARITIES, cardRarity);

    var isDark = document.documentElement.classList.contains('tw-dark');
    var inputStyle = {
      width: '100%',
      padding: '10px 12px',
      fontSize: '14px',
      borderRadius: '8px',
      border: isDark ? '1px solid #555' : '1px solid #e0e0e0',
      outline: 'none',
      backgroundColor: isDark ? '#141414' : '#fff',
      color: isDark ? '#fff' : '#1a1a1a',
    };

    if (loading) {
      return (
        <div className="tw-min-h-[calc(100vh-64px)] tw-flex tw-items-center tw-justify-center" style={{ backgroundColor: isDark ? '#111' : '#fff' }}>
          <Helmet><title>Edit Listing | Primal Marketplace</title></Helmet>
          <div className="tw-text-[#999] tw-text-sm">Loading...</div>
        </div>
      );
    }

    return (
      <div className="tw-min-h-[calc(100vh-64px)]" style={{ backgroundColor: isDark ? '#111' : '#fff' }}>
        <Helmet>
          <title>Edit Listing | Primal Marketplace</title>
        </Helmet>

        <div className="tw-max-w-lg tw-mx-auto tw-px-4 tw-pt-8 tw-pb-2">
          {/* Header */}
          <div className="tw-flex tw-items-center tw-justify-between tw-mb-8">
            <h1 className="tw-text-2xl tw-font-bold tw-text-[#1a1a1a] dark:tw-text-white tw-mb-0">
              Edit Listing
            </h1>
            <button
              type="button"
              onClick={function () { self.props.history.goBack(); }}
              className="tw-text-sm tw-font-medium tw-px-3 tw-py-1.5 tw-rounded-lg tw-border tw-border-[#e0e0e0] dark:tw-border-[#333] tw-bg-transparent tw-cursor-pointer tw-transition-colors hover:tw-border-brand-gold"
              style={{ color: '#999' }}
            >
              Cancel
            </button>
          </div>

          {/* Connected Card (read-only) */}
          {selectedCard && (
            <div className="tw-mb-6">
              <div
                className="tw-flex tw-items-center tw-gap-4 tw-p-4 tw-rounded-xl"
                style={{ backgroundColor: isDark ? '#141414' : '#f9fafb', border: isDark ? '2px solid #333' : '2px solid #fff', boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 8px 24px rgba(0,0,0,0.04)' }}
              >
                {cardImage && (
                  <img src={cardImage} alt={selectedCard.name} className="tw-w-16 tw-rounded" style={{ aspectRatio: '63/88', objectFit: 'contain' }} />
                )}
                <div className="tw-flex-1 tw-min-w-0">
                  <p className="tw-text-sm tw-font-bold tw-mb-0.5 tw-truncate" style={{ color: isDark ? '#fff' : '#1a1a1a' }}>
                    {selectedCard.name}
                    {selectedCard.variant ? ' (' + selectedCard.variant + ')' : ''}
                  </p>
                  <p className="tw-text-xs tw-mb-0" style={{ color: isDark ? '#6b7280' : '#999' }}>
                    {selectedCard.cardSet}
                    {selectedCard.rarity ? ' \u2022 ' + selectedCard.rarity : ''}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Pricing Guide */}
          {this.state.selectedCard && this.renderPricingWidget()}

          {/* Price + Quantity side-by-side */}
          <div className="tw-flex tw-gap-3 tw-mb-4">
            <div className="tw-flex-1">
              <label className="tw-block tw-text-sm tw-font-semibold tw-mb-1.5" style={{ color: isDark ? '#fff' : '#1a1a1a' }}>Price</label>
              <div className="tw-relative">
                <span className="tw-absolute tw-left-3 tw-top-1/2 tw-text-sm tw-text-[#999] tw-font-medium" style={{ transform: 'translateY(-50%)' }}>$</span>
                <input
                  type="text"
                  value={price}
                  onChange={function (e) { self.handlePriceChange(e); }}
                  onBlur={function () { self.blurPrice(); }}
                  placeholder="0.00"
                  style={Object.assign({}, inputStyle, { paddingLeft: '24px' })}
                  className="focus:tw-border-brand-gold"
                />
              </div>
              {errors.price && <p className="tw-text-red-500 tw-text-xs tw-mt-1 tw-mb-0">{errors.price}</p>}
            </div>
            <div style={{ width: '100px' }}>
              <label className="tw-block tw-text-sm tw-font-semibold tw-mb-1.5" style={{ color: isDark ? '#fff' : '#1a1a1a' }}>Qty</label>
              <input
                type="number"
                min="1"
                value={self.state.quantity}
                onChange={function (e) {
                  var val = e.target.value;
                  if (val === '' || (Number(val) >= 1 && Number(val) <= 999)) {
                    self.setState({ quantity: val });
                  }
                }}
                style={inputStyle}
                className="focus:tw-border-brand-gold"
              />
            </div>
          </div>
          <p className="tw-text-xs tw-text-[#999] tw-mt-0 tw-mb-2">
            <i className="fa fa-truck tw-mr-1" style={{ color: '#c9a84c' }}></i>
            Shipping: $1.00 flat rate (free on orders $10+). Tracking required at $50+.
          </p>
          {(function () {
            var p = Number(price);
            if (!p || p < 0.75) return null;
            var shipping = p >= 10 ? 0 : 1.00;
            var sellerAmt = (p * 0.90) + shipping;
            var payment = p + shipping + 0.50;
            var net = payment - sellerAmt;
            var procFee = (payment * 0.029) + 0.30;
            var due = net - procFee;
            if (due < 0.75) {
              var shortfall = due <= 0 ? 0.75 : 0.75 - due;
              sellerAmt = sellerAmt - shortfall;
            }
            var payout = Math.floor(sellerAmt * 100) / 100;
            var feeAmt = Math.round((p + shipping - payout) * 100) / 100;
            return (
              <div className="tw-rounded-lg tw-p-2.5 tw-mb-4" style={{ backgroundColor: isDark ? 'rgba(22,163,106,0.08)' : 'rgba(22,163,106,0.05)', border: '1px solid ' + (isDark ? 'rgba(22,163,106,0.2)' : 'rgba(22,163,106,0.15)') }}>
                <div className="tw-flex tw-justify-between tw-items-center">
                  <span className="tw-text-xs" style={{ color: isDark ? '#aaa' : '#666' }}>
                    <i className="fa fa-info-circle tw-mr-1" style={{ color: '#c9a84c' }}></i>
                    Estimated payout each
                  </span>
                  <span className="tw-text-sm tw-font-bold" style={{ color: '#16a34a' }}>${payout.toFixed(2)}</span>
                </div>
                <p className="tw-text-[10px] tw-mt-1 tw-mb-0" style={{ color: isDark ? '#777' : '#999' }}>
                  Platform fee: ${feeAmt.toFixed(2)}{shipping > 0 ? ' \u00b7 Shipping passed to you in full' : ''}
                </p>
                {feeAmt / p > 0.11 ? (
                  <p className="tw-text-[10px] tw-mt-1 tw-mb-0" style={{ color: isDark ? '#777' : '#999' }}>
                    Fee is lower when buyers purchase multiple cards from you. <a href="/faq#payout-fees" target="_blank" rel="noopener noreferrer" className="tw-no-underline" style={{ color: '#c9a84c' }}>Learn more</a>
                  </p>
                ) : null}
              </div>
            );
          })()}

          {/* Condition */}
          <div className="tw-mb-4">
            <label className="tw-block tw-text-sm tw-font-semibold tw-mb-1.5" style={{ color: isDark ? '#fff' : '#1a1a1a' }}>Condition/Foil</label>
            <select
              value={condition}
              onChange={function (e) { self.setState({ condition: e.target.value }); }}
              style={inputStyle}
              className="focus:tw-border-brand-gold"
            >
              {(function () {
                var nonFoil = conditionsList.filter(function (c) { return !c.foilOnly; });
                var foil = conditionsList.filter(function (c) { return c.foilOnly; });
                if (foil.length > 0) {
                  return [
                    <optgroup key="nf" label="Non-Foil">
                      {nonFoil.map(function (c) { return <option key={c.value} value={c.value}>{c.label}</option>; })}
                    </optgroup>,
                    <optgroup key="f" label="Foil">
                      {foil.map(function (c) { return <option key={c.value} value={c.value}>{c.label}</option>; })}
                    </optgroup>
                  ];
                }
                return nonFoil.map(function (c) { return <option key={c.value} value={c.value}>{c.label}</option>; });
              })()}
            </select>
          </div>

          {/* Title */}
          <div className="tw-mb-4">
            <label className="tw-block tw-text-sm tw-font-semibold tw-mb-1.5" style={{ color: isDark ? '#fff' : '#1a1a1a' }}>Title</label>
            <input
              type="text"
              value={title}
              onChange={function (e) { self.setState({ title: e.target.value }); }}
              style={inputStyle}
              className="focus:tw-border-brand-gold"
            />
            {errors.title && <p className="tw-text-red-500 tw-text-xs tw-mt-1 tw-mb-0">{errors.title}</p>}
          </div>

          {/* Description */}
          <div className="tw-mb-6">
            <label className="tw-block tw-text-sm tw-font-semibold tw-mb-1.5" style={{ color: isDark ? '#fff' : '#1a1a1a' }}>
              Description <span className="tw-font-normal tw-text-[#999]">(optional)</span>
            </label>
            <textarea
              value={description}
              onChange={function (e) { self.setState({ description: e.target.value }); }}
              rows={3}
              style={Object.assign({}, inputStyle, { resize: 'none' })}
              className="focus:tw-border-brand-gold"
            />
          </div>

          {/* Share & Earn Toggle + Commission */}
          {(function () {
            var priceTooLow = !self.state.price || Number(self.state.price) < SHARE_MIN_PRICE;
            var sharingEnabled = self.state.allowSharing && !priceTooLow;
            return (
          <div className="tw-mb-4">
            <div className="tw-flex tw-items-center tw-justify-between tw-mb-1.5">
              <label className="tw-block tw-text-sm tw-font-semibold tw-mb-0" style={{ color: isDark ? '#fff' : '#1a1a1a' }}>Share & Earn</label>
              <button
                type="button"
                onClick={function () { if (!priceTooLow) { self.setState({ allowSharing: !self.state.allowSharing }); } }}
                className="tw-relative tw-w-10 tw-h-5 tw-rounded-full tw-border-none tw-transition-colors tw-flex-shrink-0"
                style={{ backgroundColor: sharingEnabled ? '#c9a84c' : '#ccc', cursor: priceTooLow ? 'not-allowed' : 'pointer', opacity: priceTooLow ? 0.5 : 1 }}
              >
                <span
                  className="tw-absolute tw-top-0.5 tw-w-4 tw-h-4 tw-rounded-full tw-bg-white tw-transition-all tw-shadow-sm"
                  style={{ left: sharingEnabled ? '22px' : '2px' }}
                />
              </button>
            </div>
            {priceTooLow ? (
              <p className="tw-text-xs tw-mt-0 tw-mb-2" style={{ color: '#c9a84c' }}>Share & Earn is available for items $10.00 and above</p>
            ) : (
              <p className="tw-text-xs tw-text-[#888] tw-mt-0 tw-mb-2">Let others promote your listing and earn a commission on sales</p>
            )}
            {sharingEnabled && (
              <div>
                <label className="tw-block tw-text-xs tw-font-medium tw-mb-1" style={{ color: isDark ? '#aaa' : '#555' }}>Commission Amount</label>
                <div className="tw-relative">
                  <span className="tw-absolute tw-left-3 tw-top-1/2 tw-text-sm tw-text-[#999] tw-font-medium" style={{ transform: 'translateY(-50%)' }}>$</span>
                  <input
                    type="text"
                    value={self.state.shareCommission}
                    onChange={function (e) {
                      var val = e.target.value;
                      var re = /^(?!\.?$)\d{0,4}(\.\d{0,2})?$/;
                      if (val === '' || re.test(val)) {
                        var maxComm = Math.floor(Number(self.state.price || 0) * 0.90 * 100) / 100;
                        var numVal = Number(val);
                        if (val !== '' && numVal > maxComm) { val = String(maxComm); numVal = maxComm; }
                        self.setState({ shareCommission: val === '' ? '' : numVal || val });
                      }
                    }}
                    onBlur={function () {
                      var num = Number(self.state.shareCommission);
                      if (!num || num < 1) { self.setState({ shareCommission: 1 }); }
                    }}
                    placeholder="1.00"
                    style={Object.assign({}, inputStyle, { paddingLeft: '24px' })}
                    className="focus:tw-border-brand-gold"
                  />
                </div>
                {self.state.price ? (
                  <p className="tw-text-xs tw-mt-1 tw-mb-0" style={{ color: '#c9a84c' }}>Marketer earns ${Math.max(1, Number(self.state.shareCommission) || 1).toFixed(2)} per sale (minimum $1.00)</p>
                ) : null}
                {self.state.price ? (
                  <p className="tw-text-xs tw-text-[#888] tw-mt-0.5 tw-mb-0">Max: ${(Math.floor(Number(self.state.price) * 0.90 * 100) / 100).toFixed(2)} (your 90% earnings)</p>
                ) : null}
                <p className="tw-text-xs tw-text-[#888] tw-mt-1 tw-mb-0">Deducted from your earnings when someone buys through a share link</p>
              </div>
            )}
          </div>
            );
          })()}

          {/* Open to Offers Toggle */}
          <div className="tw-mb-4">
            <div className="tw-flex tw-items-center tw-justify-between tw-mb-1.5">
              <label className="tw-block tw-text-sm tw-font-semibold tw-mb-0" style={{ color: isDark ? '#fff' : '#1a1a1a' }}>Open to Offers</label>
              <button
                type="button"
                onClick={function () { self.setState({ acceptOffers: !self.state.acceptOffers }); }}
                className="tw-relative tw-w-10 tw-h-5 tw-rounded-full tw-border-none tw-cursor-pointer tw-transition-colors tw-flex-shrink-0"
                style={{ backgroundColor: self.state.acceptOffers ? '#c9a84c' : '#ccc' }}
              >
                <span
                  className="tw-absolute tw-top-0.5 tw-w-4 tw-h-4 tw-rounded-full tw-bg-white tw-transition-all tw-shadow-sm"
                  style={{ left: self.state.acceptOffers ? '22px' : '2px' }}
                />
              </button>
            </div>
            <p className="tw-text-xs tw-text-[#888] tw-mt-0 tw-mb-0">Allow buyers to make offers below your listed price</p>
          </div>

          {/* Condition Photos */}
          <div className="tw-mb-6">
            <label className="tw-block tw-text-sm tw-font-semibold tw-text-[#1a1a1a] dark:tw-text-white tw-mb-1">
              Condition Photos <span className="tw-font-normal tw-text-[#999]">(optional, up to 4)</span>
            </label>
            <p className="tw-text-xs tw-text-[#bbb] dark:tw-text-gray-600 tw-mb-2 tw-mt-0">
              JPG, PNG, GIF, or WebP &middot; 10 MB max
            </p>
            {this.state.photoError && (
              <p className="tw-text-red-500 tw-text-xs tw-mb-2 tw-mt-0">{this.state.photoError}</p>
            )}
            <div className="tw-grid tw-grid-cols-2 tw-gap-3">
              {photos.map(function (photo, i) {
                return (
                  <div key={i} className="tw-relative tw-aspect-square tw-rounded-xl tw-overflow-hidden tw-flex tw-items-center tw-justify-center" style={{ backgroundColor: isDark ? '#1a1a1a' : '#f3f4f6', border: isDark ? '1px solid #333' : '1px solid #e0e0e0' }}>
                    <img src={photo.preview} alt={'Photo ' + (i + 1)} className="tw-max-w-full tw-max-h-full tw-object-contain" />
                    <button
                      type="button"
                      onClick={function () { self.removePhoto(i); }}
                      className="tw-absolute tw-top-1.5 tw-right-1.5 tw-w-7 tw-h-7 tw-rounded-full tw-bg-red-600 tw-text-white tw-text-xs tw-font-bold tw-border-none tw-cursor-pointer tw-flex tw-items-center tw-justify-center"
                      style={{ lineHeight: 1 }}
                    >
                      &times;
                    </button>
                  </div>
                );
              })}
              {this.state.processingPhoto && (
                <div className="tw-aspect-square tw-rounded-xl tw-overflow-hidden tw-flex tw-items-center tw-justify-center" style={{ backgroundColor: isDark ? '#1a1a1a' : '#f3f4f6', border: isDark ? '1px solid #333' : '1px solid #e0e0e0' }}>
                  <div className="tw-flex tw-flex-col tw-items-center tw-gap-2">
                    <div className="tw-w-8 tw-h-8 tw-rounded-full tw-border-2 tw-border-[#e0e0e0]" style={{ borderTopColor: '#c9a84c', animation: 'spin 0.8s linear infinite' }} />
                    <span className="tw-text-xs tw-text-[#999]">Processing</span>
                  </div>
                </div>
              )}
              {!this.state.processingPhoto && photos.length < 4 && (
                <label className="tw-aspect-square tw-rounded-xl tw-border-2 tw-border-dashed tw-flex tw-flex-col tw-items-center tw-justify-center tw-cursor-pointer hover:tw-border-brand-gold tw-transition-colors" style={{ borderColor: isDark ? '#444' : '#d0d0d0', backgroundColor: isDark ? '#0f0f0f' : '#f9fafb' }}>
                  <i className="fa fa-camera tw-text-2xl tw-text-[#bbb] tw-mb-1" />
                  <span className="tw-text-xs tw-text-[#999] tw-font-medium">Add Photo</span>
                  <input
                    type="file"
                    accept=".jpg,.jpeg,.png,.gif,.webp"
                    onChange={function (e) { self.addPhoto(e); }}
                    style={{ display: 'none' }}
                  />
                </label>
              )}
            </div>
          </div>

          {/* Shipping info */}
          <div className="tw-mb-6">
            <div className="tw-rounded-lg tw-p-3" style={{ backgroundColor: isDark ? '#141414' : '#f9f9f9', border: isDark ? '1px solid #333' : '1px solid #e0e0e0' }}>
              <p className="tw-text-sm tw-text-[#555] dark:tw-text-[#aaa] tw-mb-0">
                <i className="fa fa-truck tw-mr-1.5" style={{ color: '#c9a84c' }}></i>
                {Number(price) >= FREE_SHIPPING_THRESHOLD
                  ? 'Free shipping. You must provide tracking when shipping this order.'
                  : 'Buyer pays $' + FLAT_SHIPPING_FEE.toFixed(2) + ' flat shipping. Tracking is optional \u2014 buyers can add it at checkout for +$5.50.'}
              </p>
            </div>
          </div>

          {/* Submit */}
          <button
            type="button"
            onClick={function () { self.handleSubmit(); }}
            disabled={loadingIcon === 'inline' || !self.hasChanges()}
            className="tw-w-full tw-font-bold tw-py-3 tw-rounded-lg tw-text-sm tw-cursor-pointer tw-border-none disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
            style={{ backgroundColor: self.hasChanges() ? '#c9a84c' : (isDark ? '#333' : '#e0e0e0'), color: '#000', transition: 'all 0.15s ease' }}
          >
            {loadingIcon === 'inline' ? 'Saving...' : self.hasChanges() ? 'Save Changes' : 'No Changes'}
          </button>

          {/* Delete */}
          <button
            type="button"
            onClick={function () { self.deleteListing(); }}
            disabled={loadingIcon === 'inline'}
            className="tw-w-full tw-py-2.5 tw-rounded-lg tw-text-sm tw-font-medium tw-cursor-pointer tw-border-none tw-transition-colors tw-mt-3 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
            style={{ backgroundColor: 'transparent', color: '#ef4444' }}
          >
            Delete Listing
          </button>

          <div className="tw-h-12" />
        </div>
      </div>
    );
  }
}

export default withAuth(EditPost);
